import moment from 'moment';
import type { InternalDataSourceConfig } from '../core/schema/types';
import { DATE_FORMAT } from '@packages/shared/constants';
import type { FetchParams } from '../hooks/useRemoteDataV2';

export function resolveRequestToken(
  token: string,
  p: FetchParams,
  pageIndex: number = p.pageIndex,
  pageSize: number = p.pageSize,
): unknown {
  switch (token) {
    case 'table.searchText':
      return p.searchText || undefined;
    case 'table.pageSize':
      return pageSize;
    case 'table.pageOffset':
      return pageIndex * pageSize;
    case 'table.sorting':
      return p.sort ? `${p.sort.field} ${p.sort.direction}` : undefined;
    default: {
      if (token.startsWith('filters.')) {
        const filterKey = token.slice('filters.'.length);
        const value = p.filters[filterKey];
        if (value === undefined || value === null || value === '') return undefined;
        return value;
      }
      return undefined;
    }
  }
}

function mappedFilterFields(requestParams: Record<string, string>): Set<string> {
  const mapped = new Set<string>();
  for (const token of Object.values(requestParams)) {
    if (typeof token === 'string' && token.startsWith('filters.')) {
      mapped.add(token.slice('filters.'.length));
    }
  }
  return mapped;
}

export function buildQueryParams(
  dataSource: Readonly<InternalDataSourceConfig>,
  p: FetchParams,
  pageIndex: number = p.pageIndex,
  pageSize: number = p.pageSize,
): Record<string, unknown> {
  const params: Record<string, unknown> = { ...dataSource.staticQueryParams };

  for (const [paramKey, tokenOrLiteral] of Object.entries(dataSource.requestParams)) {
    const resolved = resolveRequestToken(tokenOrLiteral, p, pageIndex, pageSize);
    if (resolved !== undefined && resolved !== null && resolved !== '') {
      params[paramKey] = resolved;
    }
  }

  const mappedFilters = mappedFilterFields(dataSource.requestParams);
  for (const [filterName, filterValue] of Object.entries(p.filters)) {
    if (mappedFilters.has(filterName)) continue;
    if (filterValue !== undefined && filterValue !== null && filterValue !== '') {
      params[filterName] = filterValue;
    }
  }

  return params;
}

export function getNestedValue(obj: unknown, path: string): unknown {
  if (!path || !obj || typeof obj !== 'object') return obj;
  const parts = path.split('.');
  let current: unknown = obj;
  for (const part of parts) {
    if (current == null || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return current;
}

export function formatContinuationCursorValue(
  raw: unknown,
  format: 'preserve' | 'iso' | 'mmddyyyy',
): string | undefined {
  if (raw === undefined || raw === null) return undefined;
  if (format === 'preserve') {
    if (raw instanceof Date) return raw.toISOString();
    if (typeof raw === 'string' || typeof raw === 'number' || typeof raw === 'boolean') {
      return String(raw);
    }
    return undefined;
  }
  const m = moment(raw as string | number | Date);
  if (!m.isValid()) {
    return typeof raw === 'string' ? raw : String(raw);
  }
  if (format === 'iso') return m.toISOString();
  return m.format(DATE_FORMAT);
}

/**
 * Serializes query params matching ABP/.NET convention:
 * arrays repeat the key (e.g. `Ids=1&Ids=2`) instead of bracket notation.
 */
export function serializeQueryParams(params: Record<string, unknown>): string {
  const parts: string[] = [];
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      for (const item of value) {
        if (item !== undefined && item !== null) {
          parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(item))}`);
        }
      }
    } else {
      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
    }
  }
  return parts.join('&');
}
